// Feature: docs/features/edf-generation.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Text,
  Spinner,
  useToast,
  Flex,
  IconButton,
  VStack
} from '@chakra-ui/react';
import { DownloadIcon, RepeatIcon } from '@chakra-ui/icons';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const EDFJobsTab = ({ refreshTrigger }) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const fetchJobs = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await callApi('/api/v1/edf-jobs', accessToken);
      if (!response.ok) throw new Error('Failed to fetch jobs');
      
      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (error) {
      toast({
        title: 'Error fetching jobs',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [accessToken, refreshTrigger]);

  const handleDownload = async (jobId) => {
    setDownloadingJobId(jobId);
    try {
      const response = await callApi(`/api/v1/edf-jobs/${jobId}/download`, accessToken);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get download link');
      }
      
      const data = await response.json();
      if (data.download_url) {
        // Open the pre-signed URL in a new tab to trigger download
        window.open(data.download_url, '_blank');
      }
    } catch (error) {
      toast({
        title: 'Download Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setDownloadingJobId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return <Badge colorScheme="green">Completed</Badge>;
      case 'ERROR':
      case 'FAILED':
        return <Badge colorScheme="red">Failed</Badge>;
      case 'IN_PROGRESS':
        return <Badge colorScheme="blue">In Progress</Badge>;
      case 'PENDING':
      default:
        return <Badge colorScheme="yellow">Pending</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatCaseIds = (idsString) => {
    if (!idsString) return 'None';
    const ids = idsString.split(',');
    if (ids.length <= 3) return idsString;
    return `${ids.slice(0, 3).join(', ')} ... (+${ids.length - 3} more)`;
  };

  const renderSummary = (job) => {
    if (job.result_data) {
      const { successful_count, failed_count, total_cases, error } = job.result_data;
      return (
        <VStack align="start" spacing={0}>
          <Text fontSize="sm">
            {successful_count} / {total_cases} successful
          </Text>
          {failed_count > 0 && (
            <Text fontSize="xs" color="red.500">
              {failed_count} failed
            </Text>
          )}
          {error && (
            <Text fontSize="xs" color="red.500" isTruncated maxW="200px" title={error}>
              {error}
            </Text>
          )}
        </VStack>
      );
    }

    if (job.history && job.history.length > 0) {
      // Show the most recent history message
      const latestEvent = job.history[job.history.length - 1];
      return (
        <Text fontSize="sm" color="gray.600" isTruncated maxW="250px" title={latestEvent.message}>
          {latestEvent.message}
        </Text>
      );
    }

    return <Text fontSize="sm" color="gray.400">Waiting for worker...</Text>;
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.600">
          Showing EDF generation jobs for your organization.
        </Text>
        <IconButton
          icon={<RepeatIcon />}
          size="sm"
          onClick={fetchJobs}
          isLoading={isLoading}
          aria-label="Refresh jobs"
        />
      </Flex>

      {isLoading && jobs.length === 0 ? (
        <Flex justify="center" p={8}>
          <Spinner />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Job ID</Th>
                <Th>Created At</Th>
                <Th>Case IDs</Th>
                <Th>Status</Th>
                <Th>Summary</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4}>
                    <Text color="gray.500">No jobs found.</Text>
                  </Td>
                </Tr>
              ) : (
                jobs.map((job) => (
                  <Tr key={job.job_id}>
                    <Td>{job.job_id}</Td>
                    <Td>{formatDate(job.created_at)}</Td>
                    <Td maxW="150px" isTruncated title={job.case_ids}>
                      {formatCaseIds(job.case_ids)}
                    </Td>
                    <Td>{getStatusBadge(job.status)}</Td>
                    <Td>{renderSummary(job)}</Td>
                    <Td>
                      {job.result_data && job.result_data.s3_url ? (
                        <Button
                          onClick={() => handleDownload(job.job_id)}
                          isLoading={downloadingJobId === job.job_id}
                          size="xs"
                          leftIcon={<DownloadIcon />}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Download
                        </Button>
                      ) : (
                        <Text fontSize="xs" color="gray.400">-</Text>
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default EDFJobsTab;
